<template lang="pug">
  div(
    id="pdfViewer"
    class="m-pdf-wrapper"
    :class="{ 'd-flex align-center justify-center h-75vh': loading }"
    :style="wrapperStyles"
  )
    e-progress-circular(
      v-if="loading"
      size="xl"
      width="4"
    )
    div(
      v-else
      ref="canvasContainer"
      class="canvas-container"
    )
      div(
        v-for="(canvases, idx) in dynamicPdfPages"
        :key="idx"
        ref="pagesWrapper"
        class="relative"
      )
        templates-dynamic-fields-wrapper(
          v-if="showFields"
          ref="fieldsWrapper"
          :item="item"
          :type="type"
          :page-number="idx + 1"
          :steps-count="stepsCount"
          :current-step="currentStep"
          :is-public-template="isPublicTemplate"
        )
</template>

<script>
import { mapGetters } from 'vuex'
import EProgressCircular from '~/components/elements/progress/e-progress-circular'
import TemplatesDynamicFieldsWrapper from '~/modules/templates/components/templates-dynamic-fields-wrapper'
import documentViewTypes from '~/modules/documents/mixins/documentViewTypes'
import responsive from '~/mixins/pages/responsive'
import { PARTICIPANTS_ROLES } from '~/modules/documents/constants/participants'
import { STATUSES, TYPES } from '~/modules/templates/constants/fields'

export default {
  name: 'MPdfViewer',
  components: {
    TemplatesDynamicFieldsWrapper,
    EProgressCircular
  },
  mixins: [documentViewTypes, responsive],
  props: {
    type: {
      type: String,
      required: true,
      validator: value => ['documents', 'templates'].includes(value)
    },
    item: {
      type: Object,
      default: null
    },
    src: {
      type: [String, Uint8Array],
      required: true
    },
    signaturesCount: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      canvases: [],
      pdfJsInstance: null,
      numberOfPages: null,
      wrapperStyles: {
        width: 0,
        height: '100%'
      },
      stepsCount: null,
      currentStep: null
    }
  },
  computed: {
    ...mapGetters('documents', {
      shouldRerenderDocument: 'shouldRerenderDocument'
    }),
    ...mapGetters('templatefields', {
      fields: 'fields',
      activeFieldId: 'activeFieldId',
      lastActiveFieldId: 'lastActiveFieldId',
      activeFieldOffsetX: 'activeFieldOffsetX',
      activeFieldOffsetY: 'activeFieldOffsetY',
      copiedField: 'copiedField',
      cuttedField: 'cuttedField',
      toolbarsMap: 'toolbarsMap',
      checkOnSaveValidation: 'checkOnSaveValidation',
      validationResolver: 'validationResolver'
    }),
    showFields() {
      if (this.isTemplatesType) {
        return true
      }
      return this._.get(this.item, 'canFillFields', false)
    },
    dynamicPdfPages() {
      return Array.from({ length: this.numberOfPages })
    },
    isPublicTemplate() {
      return !this._.get(this.item, 'canUpdate')
    },
    visibleTemplateFields() {
      return this._.filter(this.fields, field => field.status !== STATUSES.to_delete)
    },
    showRawValues() {
      if (!this.visibleTemplateFields) {
        return false
      }
      return this.visibleTemplateFields.every(
        field => field.documentFieldPreValue && !field.documentFieldValue
      )
    }
  },
  watch: {
    signaturesCount: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.loadPdf()
        }
      },
      immediate: true
    },
    shouldRerenderDocument: {
      async handler(val) {
        if (val) {
          await this.loadPdf()
          this.renderTemplateFields()
          this.$store.commit('documents/SET_SHOULD_RERENDER_DOCUMENT', false)
        }
      }
    },
    checkOnSaveValidation: {
      async handler(val) {
        if (val) {
          await this.validateFields()
          this.$store.commit('templatefields/CHECK_ON_SAVE_VALIDATION', false)
        }
      }
    }
  },
  created() {
    // TODO: separate to other component dynamic fields-wrapper in future
    this.renderTemplateFields()
  },
  mounted() {
    if (this.isTemplatesType) {
      this.onDropareaKeydown()
    }
  },
  beforeDestroy() {
    this.pdfJsInstance.destroy()
  },
  methods: {
    async loadPdf() {
      const pdfJs = window['pdfjs-dist/build/pdf']
      pdfJs.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.worker.min.js'

      try {
        this.loading = true
        this.pdfJsInstance = pdfJs.getDocument(this.src)
        const loadedPdf = await this.pdfJsInstance.promise
        this.numberOfPages = loadedPdf.numPages
        this.canvases = Array.from({ length: this.numberOfPages }, () =>
          document.createElement('canvas')
        )

        for (let pageNumber = 1; pageNumber <= this.numberOfPages; pageNumber++) {
          const page = await loadedPdf.getPage(pageNumber)
          const width = this._.get(page, 'view[2]')
          this.scale = width > 1000 ? 0.5 : 1.3
          this.$store.commit('documents/SET_DOCUMENT_SCALE', this.scale)
          const viewport = page.getViewport({ scale: this.scale })
          const canvas = this.canvases[pageNumber - 1]
          const context = canvas.getContext('2d', { willReadFrequently: this.numberOfPages > 1 })
          canvas.width = viewport.width
          canvas.height = viewport.height
          this.wrapperStyles.width = `${viewport.width}px`
          this.wrapperStyles.height =
            this.xsDevice || this.smDevice || this.mdDevice ? '100%' : `${viewport.height}px`

          const renderContext = {
            canvasContext: context,
            viewport
          }

          await page.render(renderContext).promise
        }
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this._.forEach(this.canvases, (canvas, idx) => {
            if (this.xsDevice || this.smDevice || this.mdDevice) {
              canvas.style.width = '100%'
            }
            const pageNumber = idx + 1
            this.handleCanvasEvents(canvas, pageNumber)
            this.$refs.pagesWrapper[idx].appendChild(canvas)
          })
          this.showFieldsGuide()
        })
      }
    },
    handleCanvasEvents(canvasElem, pageNumber) {
      canvasElem.addEventListener('dragover', e => {
        e.preventDefault()
      })
      canvasElem.addEventListener('drop', e => {
        const rect = canvasElem.getBoundingClientRect()
        const transferedFieldType = e.dataTransfer.getData('text')
        // case for existing fields
        if (this.activeFieldId) {
          this.$store.commit('templatefields/SET_DRAGGING_MAP_FIELD_ID', {
            id: this.activeFieldId,
            value: false
          })
          const positionX = e.clientX - this.activeFieldOffsetX - rect.left
          const positionY = e.clientY - this.activeFieldOffsetY - rect.top
          const field = this._.find(this.fields, field => field.id === this.activeFieldId)
          this.$store.commit('templatefields/UPDATE_FIELD', {
            id: this.activeFieldId,
            updatedData: {
              positionX,
              positionY,
              changed: true,
              changedPosition: true,
              page: pageNumber,
              changedPage: field.page !== pageNumber
            }
          })
          this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', null)
          return
        }
        // case for new fields
        if (transferedFieldType) {
          const fieldOverrides = {
            positionX: e.clientX - rect.left,
            positionY: e.clientY - rect.top
          }
          this.$store.dispatch('templatefields/createField', {
            type: transferedFieldType,
            page: pageNumber,
            overrides: fieldOverrides
          })
        }
      })
      // to hide prev active item toolbar
      canvasElem.addEventListener('click', e => {
        const isCanvasClick = e.target instanceof HTMLCanvasElement
        if (isCanvasClick && this.toolbarsMap[this.lastActiveFieldId]) {
          this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
            id: this.lastActiveFieldId,
            value: false
          })
        }
      })
    },
    onDropareaKeydown() {
      document.addEventListener('keydown', e => {
        if (e.ctrlKey && e.key === 'v') {
          e.preventDefault()
          if (this.copiedField) {
            this.$store.dispatch('templatefields/pasteField', this.copiedField)
          }
          if (this.cuttedField) {
            this.$store.dispatch('templatefields/pasteField', this.cuttedField)
          }
        }
      })
    },
    renderTemplateFields() {
      // reset fields first before rendering to be sure that duplicates wont occur
      this.$store.commit('templatefields/RESET_FIELDS')
      const fields = this._.get(this.item, 'fields', [])
      this._.forEach(fields, field => {
        let data
        if (this.isTemplatesType) {
          data = { ...field, status: STATUSES.created, changed: false }
        } else if (this.isDocumentsType) {
          data = {
            ...field.extraData,
            documentFieldId: field.id,
            documentFieldValue: field.value,
            documentFieldPreValue: field.preValue
          }
        }
        if (!this._.some(this.fields, f => f.id === field.id)) {
          this.$store.commit('templatefields/ADD_FIELD', data)
        }
      })
    },
    async showFieldsGuide() {
      const showGuide =
        this._.get(this.item, 'currentUser.role') === PARTICIPANTS_ROLES.SIGNER ||
        this._.get(this.item, 'currentUser.role') === PARTICIPANTS_ROLES.FILLER
      const isSomeEmptyField = this._.some(this.fields, field => !field.documentFieldValue)
      const documentForFillOrSign =
        this.isDocumentsType && showGuide && isSomeEmptyField && !this.showRawValues
      const publicTemplate = this.isTemplatesType && this.isPublicTemplate
      if (documentForFillOrSign || publicTemplate) {
        const fieldsRefs = this._.map(
          this.$refs.fieldsWrapper,
          fieldWrapperComponent => fieldWrapperComponent.$refs.dynamicFields
        )
        const flattenFieldsRefs = this._.flatten(fieldsRefs)
        const filteredFieldsRefs = this._.filter(
          flattenFieldsRefs,
          fieldRef => fieldRef && fieldRef.field.type !== TYPES.formula && fieldRef.field.required
        )
        this.stepsCount = filteredFieldsRefs && filteredFieldsRefs.length
        for (const [idx, fieldInstance] of filteredFieldsRefs.entries()) {
          if (fieldInstance.isFilled) {
            continue
          }
          this.currentStep = idx + 1
          await fieldInstance.showGuide()
        }
      }
    },
    async validateFields() {
      const fieldsRefs = this._.map(
        this.$refs.fieldsWrapper,
        fieldWrapperComponent => fieldWrapperComponent.$refs.dynamicFields
      )
      const flattenFieldsRefs = this._.flatten(fieldsRefs)
      const filteredFieldsRefs = this._.filter(flattenFieldsRefs, fieldRef => fieldRef)
      const promises = this._.map(filteredFieldsRefs, fieldRef => fieldRef.validate())
      const validationResults = await Promise.all(promises)
      const hasSomeValidationErrors = this._.some(validationResults, hasError => hasError)
      if (typeof this.validationResolver === 'function') {
        this.validationResolver(!hasSomeValidationErrors)
      }
    }
  }
}
</script>

<style lang="scss">
.m-pdf-wrapper {
  @media (max-width: map-get($breakpoints, 'md')) {
    overflow-y: auto;
    overflow-x: auto;
  }

  @media (max-width: map-get($breakpoints, 'sm')) {
    overflow-y: unset;
    overflow-x: hidden;
    overscroll-behavior: contain;
  }
}

.ghost {
  position: absolute;
  background: rgba(255, 255, 255, 0.5);
  pointer-events: none;
  opacity: 0.5;
  border-radius: 4px;
  border: 1px solid $danger;
}
</style>
